/**
 * Item Detail, third pass — Lab page.
 *
 * A working version of the third-pass mockup of 2026-10-14
 * (claude.ai/artifact/Dxsg9MdYXNp6Ma5HPNBUr1): six destinations that never
 * disappear, standing that names its list, the cycle → track → session tree
 * with where each setting comes from, AI beside the choice it helps with, and
 * what happens when you edit what the item says (a new version).
 *
 * SAMPLE item only (./thirdPassSample.ts); nothing is read from or written to
 * the workspace. The markup lives in ItemDetailThirdPassView, which the real
 * item page (/prioritization/items/:itemId) renders with real data — so this
 * page and the real one look the same by construction. The second pass stays
 * at /elevation/lab/item-detail-second-pass.
 */
import { ObjectRef } from '@/components/ds/ObjectRef';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LABEL_MONO } from '@/components/mono/primitives';
import { ItemDetailThirdPassView, type ItemDetailViewModel } from './ItemDetailThirdPassView';
import { THIRD_PASS as S } from './thirdPassSample';
import { LINEAGE_ACTS, RELATIONSHIP_KINDS } from './useItemDetailModel';

interface SampleState {
  title: string;
  description: string;
  version: number;
  why: string;
  aiDecision: 'open' | 'accepted' | 'dismissed';
}

const LINEAGE_RECORDED: Record<string, string> = {
  merge: 'Merged in Valve supplier backup plan · 2 Aug',
  fork: 'Forked into Tooling transfer variant · 9 Sep',
};
const RELATIONSHIP_RECORDED: Record<string, string[]> = {
  package: ['With Tooling transfer · Supplier move'],
  sequence: ['Before this: PPAP sign-off', 'Waiting on this: Dual-source contract'],
  collection: ['Ops risk review — Sep'],
};

/** A sample row in the same shape the real page builds: what the act or link does, what is recorded, and its action. */
function sampleRow(label: string, does: string, recorded: string[], action: string) {
  return {
    kind: label,
    text: (
      <>
        {recorded.length === 0 && <span className="block text-[var(--text-dim)]">None</span>}
        {recorded.map((r) => (
          <span key={r} className="block text-[var(--text-pri)] [&+&]:mt-1">
            {r}
          </span>
        ))}
        <span className="mt-1 block text-[12.5px] leading-snug text-[var(--text-dim)]">{does}</span>
      </>
    ),
    actions: [{ label: action }],
  };
}

const MORE = ['Print view', 'Integrations · 1', 'Who can see it', 'Copy link', 'Split, merge or fork', 'Retire'];

/** The sample item as the view's model. Handlers change only this page's state. */
function buildSampleModel(st: SampleState, set: (patch: Partial<SampleState>) => void): ItemDetailViewModel {
  const { version, aiDecision } = st;
  return {
    badge: { category: 'Commitment', altitude: aiDecision === 'accepted' ? 3 : 4 },
    state: { label: '● Active', tone: 'green' },
    owner: S.owner,
    version,
    askAi: null,
    discuss: { count: 3 },
    watch: { watching: false },
    more: MORE.map((label) => ({ label, danger: label === 'Retire' })),
    title: st.title,
    description: st.description,
    // The sample is linked to Jira: its title is kept there, its description is shared.
    fieldSources: {
      title: [{ field: 'title', connectorType: 'jira', systemName: 'Jira', keptIn: 'there', direction: 'in', editableHere: false, externalUrl: null, sentence: 'The title is kept in Jira and copied here. Change it in Jira.' }],
      description: [{ field: 'description', connectorType: 'jira', systemName: 'Jira', keptIn: 'shared', direction: 'in', editableHere: true, externalUrl: null, sentence: 'The description is shared with Jira. Changes made in Jira come here; changes made here are not sent to Jira yet.' }],
    },
    titleEdit: {
      version,
      canEdit: true,
      askWhy: true,
      notice: (v) =>
        `${S.groupsOnRankedVersion} groups ranked version ${v} in Q3 FY26, and they keep seeing version ${v} with their results. A session that starts after this sees version ${(v ?? 0) + 1}. If the change is more than wording, say why so the cycle owner can decide whether to re-rank.`,
      onSave: async (title, description, why) => {
        set({ title, description, why, version: version + 1 });
      },
    },
    standing: S.standing,
    attention: S.attention,
    attentionNote: 'No completeness percentage. Readiness depends on the evaluation, so each line names the one it comes from.',
    whatItIs: S.whatItIs.map((w) => ({ label: w.label, text: w.text, ai: w.ai })),
    proposal: {
      label: '✦ Recommended · altitude',
      note: 'Recommend only — a person decides',
      from: 'Altitude 4 · Portfolio',
      to: 'Altitude 3 · Program',
      why: 'Why: the work is one supplier qualification inside the Supply resilience programme, itself altitude 5. Evidence: the programme’s breakdown lists this as one of four parts. Confidence 0.71 is the model’s own estimate, not a measured accuracy.',
      decided: aiDecision !== 'open',
      decidedText:
        aiDecision === 'accepted'
          ? `Accepted. Altitude is 3 and this is version ${version}. History records it as an agent’s suggestion accepted by you.`
          : 'Dismissed. History records that you dismissed the suggestion.',
      actions: [
        { label: 'Accept', onClick: () => set({ aiDecision: 'accepted', version: version + 1 }) },
        { label: 'Edit, then accept' },
        { label: 'Dismiss', onClick: () => set({ aiDecision: 'dismissed' }) },
      ],
      footnote: `Accepting changes this item’s altitude, which makes version ${version + 1}. It stays inside the Platform track’s range (3–4).`,
    },
    assessment: {
      intro: (
        <>
          Showing the <strong className="text-[var(--text-pri)]">Engineering 2026</strong> value collection (owner: Ray Delgado). An attribute
          becomes a criterion when an evaluation chooses it. Select a row to see its scale, who set it, contributions and frozen copies.
        </>
      ),
      rows: S.assessment,
      initialOpen: 'Reach',
      detailActions: () => [{ label: 'Link evidence' }, { label: 'Contribute a value' }, { label: 'Recommend a value from sources', ai: true }],
    },
    evidence: {
      add: {},
      rows: [
        { kind: 'Document', text: 'CAPA-118 corrective action', meaning: '· supports Risk reduction, Reach', verdict: 'Verified', tone: 'green' },
        { kind: 'Datadog', text: 'Valve failure incidents, last 90 days', meaning: '· supports Risk reduction', verdict: 'Unverified', tone: 'neutral' },
      ],
    },
    strategy: {
      rows: [
        {
          kind: 'Strategy',
          text: (
            <>
              <ObjectRef kind="strategy" name="Resilient operations 2026" hideWord />
              <div className="mt-1.5 space-y-2 border-l pl-3" style={{ borderColor: 'var(--rule)' }}>
                <div className="py-1">
                  <div className="flex flex-wrap items-baseline gap-x-1.5 text-[12.5px] text-[var(--text-dim)]">
                    <ObjectRef kind="objective" name="Supply chain that bends, not breaks" hideWord />
                    <span aria-hidden>›</span>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-1.5">
                    <ObjectRef kind="objective" name="No line stops for a single supplier" />
                    <span className="text-[13px] text-[var(--text-dim)]">· this work mainly supports it</span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-baseline gap-x-1.5 text-[13px]">
                    <ObjectRef kind="goal" name="Dual-sourced critical parts" />
                    <span className="text-[var(--text-dim)]">now 62% · target 90% · this work moves it</span>
                  </div>
                </div>
              </div>
            </>
          ),
        },
        { kind: 'Link another', text: <span className="text-[var(--text-dim)]">Another objective, or a goal under one, that this work supports.</span>, actions: [{ label: 'Link' }] },
      ],
    },
    relationships: { rows: RELATIONSHIP_KINDS.map((x) => sampleRow(x.label, x.does, RELATIONSHIP_RECORDED[x.kind] ?? [], 'Add')) },
    links: { rows: [], add: null, find: {} },
    commitments: { rows: [{ kind: 'Commitment', text: 'Second source qualified through PPAP by 30 Nov · for Operations', verdict: 'Covered', tone: 'green' }] },
    people: S.people,
    prioritization: {
      intro:
        'Cycle → track → session. Settings can come from the workflow, the cycle, the track or the session; each shows where it came from. Change them on that level’s Defaults tab — it affects everything in its pool.',
      cycles: S.cycles,
      loose: [{ title: 'No cycle · Supplier scenarios', standing: 'Scenario pool', tone: 'neutral', text: 'An item pool used to try scenarios. No criteria, so nothing to rank against yet.' }],
    },
    delivery: [{ title: 'In the plan and how it turned out', owned: 'same units on both sides', rows: S.delivery }],
    versions: {
      rows: [
        ...(version > S.version
          ? [{ kind: `v${version} · just now`, text: 'Saved on this page.', meaning: st.why ? `“${st.why}”` : '', verdict: 'current', tone: 'neutral' as const }]
          : []),
        ...S.versions.map((v, i) => (i === 0 && version > S.version ? { ...v, verdict: undefined } : v)),
      ],
    },
    actors: { rows: S.actors },
    identity: { journey: {}, rows: [], empty: 'Not promoted from a session, and never retired.' },
    lineage: { rows: LINEAGE_ACTS.map((x) => sampleRow(x.label, x.does, LINEAGE_RECORDED[x.type] ? [LINEAGE_RECORDED[x.type]] : [], x.label)) },
    rail: [
      { h: 'At a glance', rows: [{ k: 'Category', v: 'Commitment · +2' }, { k: 'Altitude', v: aiDecision === 'accepted' ? '3 · Program' : '4 · Portfolio' }, { k: 'Owner', v: S.owner }, { k: 'State', v: 'Active' }, { k: 'Source', v: 'Intake · drafted by agent, reviewed' }, { k: 'Version', v: String(version) }] },
      { h: 'Where it sits', rows: [{ k: 'Queue', v: 'General' }, { k: 'Product', v: 'Not placed on the product map' }, { k: 'Evaluations', v: '2 cycles · 2 tracks · 2 sessions' }] },
      { h: 'Lists it’s in', rows: [{ k: 'Collections', v: '1' }, { k: 'Item pools', v: '3' }, { k: 'Results', v: '2 sessions' }, { k: 'Priority lists', v: '1 · #3' }, { k: 'Plans', v: '1' }] },
    ],
  };
}

export function ItemDetailThirdPassLab() {
  const [st, setSt] = useState<SampleState>({ title: S.title, description: S.description, version: S.version, why: '', aiDecision: 'open' });
  const model = buildSampleModel(st, (patch) => setSt((cur) => ({ ...cur, ...patch })));

  return (
    <div className="mx-auto max-w-[1260px] pb-24">
      <header className="border-b pb-5 pt-2" style={{ borderColor: 'var(--rule)' }}>
        <div className={LABEL_MONO}>Lab · item detail, third pass · sample item · writes nothing</div>
        <h1 className="mt-2 font-serif text-[30px] font-semibold leading-tight" style={{ color: 'var(--text-pri)' }}>
          Item detail, organized by what each part is for
        </h1>
        <p className="mt-2 max-w-[80ch] text-[var(--text-sec)]">
          One item, always the same record. Six destinations that never disappear. Ranking adds standing in a named list; it never changes
          what the item is. Try the tabs, More, Edit on the title, a row in Assessment, and the AI proposal on Overview.
        </p>
        <div className="mt-3 flex flex-wrap gap-4">
          <Link to="/elevation/lab/item-detail-second-pass" className="font-mono text-[12px] uppercase tracking-[0.07em] text-[var(--text-ter)] underline underline-offset-[3px]">
            Second pass →
          </Link>
          <Link to="/prioritization/items" className="font-mono text-[12px] uppercase tracking-[0.07em] text-[var(--text-ter)] underline underline-offset-[3px]">
            Today’s item page →
          </Link>
        </div>
      </header>

      <ItemDetailThirdPassView model={model} />
    </div>
  );
}

export default ItemDetailThirdPassLab;
